import {
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useHistory } from 'react-router';
import { APIService } from 'src/api/api';
import ROUTE from 'src/utils/routeConstants';
import { getFuelQuantity } from 'src/utils/getInitials';

const getQuantity = (value, configuredValue) => getFuelQuantity(Number(value) || 0, configuredValue);

const ReconciliationSummary = ({ sx }) => {
  const theme = useTheme();
  const history = useHistory();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setHasError(false);

    try {
      const response = await APIService.getLatestReconciliationData();
      if (!response?.success) {
        throw new Error('Unable to load reconciliation data');
      }

      const reportList = response.result?.results || response.result || [];
      setReports(Array.isArray(reportList) ? reportList : []);
    } catch (error) {
      console.error('Dashboard reconciliation error:', error);
      setHasError(true);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: '0 4px 24px 0 rgba(0,0,0,0.06)',
        overflow: 'hidden',
        ...sx
      }}
    >
      <CardHeader
        sx={{
          p: 2.5,
          pb: 1.5,
          '& .MuiCardHeader-action': { m: 0 }
        }}
        avatar={(
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              display: 'flex'
            }}
          >
            <AssessmentOutlinedIcon fontSize="small" />
          </Box>
        )}
        title={(
          <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
            LATEST RECONCILIATION
          </Typography>
        )}
        subheader={(
          <Typography variant="caption" color="text.secondary">
            One latest report for each dispenser
          </Typography>
        )}
        action={(
          <Button
            size="small"
            endIcon={<ArrowForwardRoundedIcon fontSize="small" />}
            onClick={() => history.push(ROUTE.reconciliationReport)}
            sx={{ textTransform: 'none', fontWeight: 700, mt: 0.5 }}
          >
            View report
          </Button>
        )}
      />
      <Divider />

      {loading && (
        <Stack spacing={1.5} sx={{ p: 2.5 }}>
          {[1, 2, 3].map((row) => (
            <Box
              key={row}
              sx={{
                height: 44,
                borderRadius: 1.5,
                bgcolor: theme.palette.action.hover
              }}
            />
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
            <CircularProgress size={20} />
          </Box>
        </Stack>
      )}
      {!loading && hasError && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            Reconciliation data is unavailable.
          </Typography>
          <Button size="small" variant="outlined" onClick={fetchReports}>
            Try again
          </Button>
        </Box>
      )}
      {!loading && !hasError && reports.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No reconciliation records found.
          </Typography>
        </Box>
      )}
      {!loading && !hasError && reports.length > 0 && (
        <TableContainer sx={{ maxHeight: 430, overflowX: 'auto' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 660 }}>
            <TableHead>
              <TableRow>
                {[
                  { label: 'Dispenser', align: 'left' },
                  { label: 'Opening', align: 'right' },
                  { label: 'Inward', align: 'right' },
                  { label: 'Dispensed', align: 'right' },
                  { label: 'Stock', align: 'right' }
                ].map((column) => (
                  <TableCell
                    key={column.label}
                    align={column.align}
                    sx={{
                      bgcolor: '#F9FAFB',
                      color: 'text.secondary',
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: 0.35,
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow
                  hover
                  key={report.id}
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    '&:hover': { bgcolor: '#F9FAFB' }
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, maxWidth: 190 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      noWrap
                      title={report.dispenser_tank_name || 'Unassigned tank'}
                    >
                      {report.dispenser_tank_name || 'Unassigned tank'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{getQuantity(report.opening_value, report.opening_value_gallons)}</TableCell>
                  <TableCell align="right">{getQuantity(report.inward, report.inward_gallons)}</TableCell>
                  <TableCell align="right">{getQuantity(report.sales, report.sales_gallons)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {getQuantity(report.stock, report.stock_gallons)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
};

ReconciliationSummary.propTypes = {
  sx: PropTypes.object
};

export default memo(ReconciliationSummary);
